import type { SizeType } from '@atlaskit/avatar';
import { Box, xcss } from '@atlaskit/primitives';
import EllipsisOverflow from '@databases/field-value/common/EllipsisOverflow';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { PLACEHOLDER_DISPLAY_NAMES } from '@databases/templates/types';
import { type KnownUser } from '@databases/types/confluence';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { loadUserById } from '@databases/utils/redux/users/actions';
import { selectUserByIdOptional } from '@databases/utils/redux/users/selectors';
import React, { useCallback, type FunctionComponent, type ReactNode } from 'react';

import { AvatarWithLabel } from './AvatarWithLabel';

type User = KnownUser | { accountId: string; displayName: string };

interface UserAvatarProps {
  user: User;
  avatarWrapper?: (avatar: ReactNode) => ReactNode;
  size?: SizeType;
  avatarOnly?: boolean;
}

const UserMention = ({
  user,
  isCurrentUser,
  showDisabledValue,
}: {
  user: User;
  isCurrentUser: boolean;
  showDisabledValue?: boolean;
}) => {
  const userMentionStyle = xcss({
    display: 'inline-flex',
    backgroundColor: isCurrentUser
      ? 'color.background.brand.bold'
      : 'color.background.neutral',
    color: showDisabledValue
      ? 'color.text.disabled'
      : isCurrentUser
      ? 'color.text.inverse'
      : 'color.text.subtle',
    borderRadius: 'radius.full',
    paddingTop: '0',
    paddingRight: 'space.075',
    paddingBottom: '0',
    paddingLeft: 'space.075',
    whiteSpace: 'nowrap',
    maxWidth: 'calc(100% - 10px)',
  });
  return (
    <Box
      as="span"
      data-mention-id={user.accountId}
      xcss={userMentionStyle}
      testId="user-mention"
    >
      <EllipsisOverflow>
        {
          // Won't translate @
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        }
        @{user.displayName}
      </EllipsisOverflow>
    </Box>
  );
};

const UserAvatar = ({
  user,
  avatarWrapper,
  size = 'small',
  avatarOnly = false,
}: UserAvatarProps) => {
  return (
    <AvatarWithLabel
      userOrGroup={'profilePicture' in user ? user : undefined}
      label={user.displayName}
      avatarWrapper={avatarWrapper}
      size={size}
      avatarOnly={avatarOnly}
    />
  );
};

const DUMMY_PREFIX = 'dummy-';

const getPlaceholderDisplayNameForTemplates = (accountId: string) => {
  return accountId in PLACEHOLDER_DISPLAY_NAMES
    ? PLACEHOLDER_DISPLAY_NAMES[accountId]
    : PLACEHOLDER_DISPLAY_NAMES[`${DUMMY_PREFIX}0`];
};

interface UserNameProps {
  accountId: string;
  variant: 'mention' | 'avatar';
  /** If specified, will be shown as the display name until the account is loaded. */
  displayName?: string;
  currentUserId?: string;
  showDisabledValue?: boolean;
  avatarWrapper?: (avatar: ReactNode) => ReactNode;
  size?: SizeType;
  avatarOnly?: boolean;
}

/**
 * Displays a users username, avatar, or mention using the
 * specified accountId and variant. Uses the REST API to load the
 * user details and stores them in the redux store.
 */
export const UserCard: FunctionComponent<UserNameProps> = ({
  accountId,
  displayName,
  currentUserId,
  variant,
  showDisabledValue,
  avatarWrapper,
  size = 'small',
  avatarOnly = false,
}) => {
  const [user, isCurrentUser] = defaultStoreUtils.useEqualSelector((state) => {
    const viewingUserId = currentUserId ?? selectCurrentUserId(state);
    const user = selectUserByIdOptional(state, { accountId });
    const displayNameForTemplates = accountId.startsWith(DUMMY_PREFIX)
      ? getPlaceholderDisplayNameForTemplates(accountId)
      : displayName;

    return [
      user ??
        (displayNameForTemplates
          ? { accountId, displayName: displayNameForTemplates }
          : undefined),
      !!viewingUserId && accountId === viewingUserId,
    ] as const;
  });

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      await dispatch(loadUserById(accountId));
    }, [accountId, dispatch])
  );

  if (!user) {
    return null;
  }

  if (variant === 'avatar') {
    return (
      <UserAvatar
        user={user}
        avatarWrapper={avatarWrapper}
        size={size}
        avatarOnly={avatarOnly}
      />
    );
  }

  if (variant === 'mention') {
    return (
      <UserMention
        user={user}
        isCurrentUser={isCurrentUser}
        showDisabledValue={showDisabledValue}
      />
    );
  }
};
